import { useParams } from "react-router-dom";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import queryString from "query-string";
import { KeyedMutator, SWRResponse } from "swr";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import { V1TableListEnvelope, V1TableResult } from "~/types/interfaces/catalogTables";

const { VITE_CATALOG_API } = import.meta.env;

export const PAGE_SIZE = 10;

// Both v1 table endpoints this hook calls — GET /v1/datasets/{id}/tables (Postgres,
// always current) and GET /v1/tables/search (OpenSearch full-text) — return the shared
// TableResult item in the same envelope. This view reads only the listing fields; the
// search payload additionally carries score/highlight/tags, which it ignores.
type DatasetTableListResponse = V1TableListEnvelope<V1TableResult>;

export const adaptItem = (item: V1TableResult): DatasetTableResponse => ({
  name: item.name,
  description: item.description ?? "",
  fullyQualifiedName: item.fqn,
  database_schema: item.database_schema,
  tableType: item.table_type,
  accessLevel: item.access_level,
});

const useDatasetTables = (
  searchTerm: string = "",
  id?: string
): SWRResponse<DatasetTableResponse[]> & { totalCount?: number } => {
  const { datasetId = id } = useParams();
  const debouncedSearch = useDebounce(searchTerm);
  const [query] = useQueryParams({
    perPage: withDefault(NumberParam, PAGE_SIZE),
    page: withDefault(NumberParam, 0),
  });
  const { page, perPage } = query;
  const offset = page ? (page - 1) * perPage : 0;

  // With a search term, use the v1 search endpoint (OpenSearch) for its full-text
  // search capabilities, scoped to this dataset. The OpenSearch index is eventually
  // consistent, with up to 5-minute lag as of this writing.
  const searchQs = queryString.stringify({
    query_term: debouncedSearch,
    dataset_id: datasetId,
    offset,
    limit: perPage,
  });

  // No search term: read the always-current Postgres endpoint so freshly created
  // or removed tables show up immediately, side-stepping the OpenSearch index lag.
  const listQs = queryString.stringify({
    offset,
    limit: perPage,
    sort: "name",
  });

  const key = datasetId
    ? debouncedSearch
      ? `${VITE_CATALOG_API}/v1/tables/search?${searchQs}`
      : `${VITE_CATALOG_API}/v1/datasets/${datasetId}/tables?${listQs}`
    : null;

  // Both endpoints return the same `items`/`metadata` envelope, so a single adapter
  // handles either payload — including the transient window where `keepPreviousData`
  // serves the previous branch's data while the SWR key switches.
  const resp = useFetcher<DatasetTableListResponse>(key, {
    keepPreviousData: true,
  });

  return {
    ...resp,
    data: resp.data ? resp.data.items.map(adaptItem) : undefined,
    totalCount: resp.data?.metadata.total_count,
    isLoading: resp.isLoading || Boolean(searchTerm && searchTerm !== debouncedSearch),
    mutate: resp.mutate as KeyedMutator<any>,
  } as SWRResponse<DatasetTableResponse[]> & { totalCount?: number };
};

export default useDatasetTables;
